<script setup lang="ts">
import { ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import axios from 'axios';

const props = defineProps({
  email: { type: String, required: true },
  context: { type: String, default: 'login' },
  visible: { type: Boolean, default: true },
});
const emit = defineEmits(['verified', 'close']);

const otp = ref('');
const loading = ref(false);
const resendLoading = ref(false);
const error = ref('');
const success = ref(false);
const resendTimer = ref(0);
let timer: any = null;

const submitOTP = async () => {
  if (!otp.value || otp.value.length !== 6) {
    error.value = 'Please enter the 6-digit OTP.';
    return;
  }
  loading.value = true;
  error.value = '';
  try {
    const response = await axios.post(`${import.meta.env.VITE_ONBOARDING_SERVICE}verify_otp`, {
      email: props.email,
      otp: otp.value,
      context: props.context,
    });
    if (response.data.status) {
      success.value = true;
      emit('verified');
    } else {
      error.value = response.data.message || 'Invalid OTP. Please try again.';
    }
  } catch (e) {
    error.value = 'Verification failed. Please try again.';
  } finally {
    loading.value = false;
  }
};

const resendOTP = async () => {
  resendLoading.value = true;
  error.value = '';
  try {
    await axios.post(`${import.meta.env.VITE_ONBOARDING_SERVICE}send_otp`, {
      email: props.email,
      context: props.context,
    });
    ElMessage.success('OTP resent!');
    resendTimer.value = 60;
    timer = setInterval(() => {
      resendTimer.value--;
      if (resendTimer.value <= 0) clearInterval(timer);
    }, 1000);
  } catch (e) {
    error.value = 'Failed to resend OTP.';
  } finally {
    resendLoading.value = false;
  }
};

watch(() => props.visible, (val) => {
  if (!val) {
    otp.value = '';
    error.value = '';
    success.value = false;
    resendTimer.value = 0;
    if (timer) clearInterval(timer);
  }
});
</script>

<template>
  <div v-if="visible" class="otp-wrapper">
    <div class="otp-container">

      <!-- Icon -->
      <div class="otp-icon">✉</div>

      <h2 class="otp-title">Check your email</h2>
      <p class="otp-subtitle">
        We sent a 6-digit code to<br>
        <strong>{{ email }}</strong>
      </p>

      <!-- OTP input -->
      <div class="otp-input-group">
        <input
          v-model="otp"
          maxlength="6"
          placeholder="000000"
          class="otp-input"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          autocomplete="one-time-code"
          @keyup.enter="submitOTP"
        />
      </div>

      <div v-if="error" class="otp-error">
        <span class="error-icon">!</span> {{ error }}
      </div>

      <button class="verify-btn" @click="submitOTP" :disabled="loading || otp.length !== 6">
        <span v-if="!loading">Verify code</span>
        <span v-else>Verifying…</span>
      </button>

      <div class="otp-actions">
        <button
          class="resend-btn"
          @click="resendOTP"
          :disabled="resendTimer > 0 || resendLoading"
        >
          <span v-if="resendLoading">Sending…</span>
          <span v-else-if="resendTimer > 0">Resend in {{ resendTimer }}s</span>
          <span v-else>Resend code</span>
        </button>

        <span class="divider">·</span>

        <button class="cancel-btn" @click="$emit('close')">Cancel</button>
      </div>

    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Serif+Display&display=swap');

.otp-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100%;
  font-family: 'DM Sans', sans-serif;
  animation: fadeUp 0.4s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

.otp-container {
  width: 100%;
  max-width: 380px;
  text-align: center;
}

.otp-icon {
  width: 56px;
  height: 56px;
  background: #12284A;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  margin: 0 auto 1.5rem;
}

.otp-title {
  font-family: inherit;
  font-size: 2rem;
  font-weight: 700;
  color: #0f1c2e;
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.02em;
}

.otp-subtitle {
  font-size: 0.9rem;
  color: #8a94a6;
  margin: 0 0 2rem 0;
  line-height: 1.6;
}

.otp-subtitle strong {
  color: #374151;
  font-weight: 500;
}

.otp-input-group {
  margin-bottom: 1rem;
}

.otp-input {
  width: 100%;
  height: 56px;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  font-family: 'DM Sans', sans-serif;
  font-size: 1.6rem;
  font-weight: 500;
  letter-spacing: 0.4em;
  text-align: center;
  color: #0f1c2e;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}

.otp-input:focus {
  border-color: #12284A;
}

.otp-input::placeholder {
  color: #d1d5db;
  letter-spacing: 0.3em;
}

.otp-error {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: #fff5f5;
  border: 1px solid #fdd;
  border-radius: 8px;
  padding: 0.65rem 1rem;
  font-size: 0.85rem;
  color: #c0392b;
  margin-bottom: 1rem;
  text-align: left;
}

.error-icon {
  width: 16px; height: 16px; background: #e74c3c; color: white;
  border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
  font-size: 0.65rem; font-weight: 700; flex-shrink: 0;
}

.verify-btn {
  width: 100%;
  height: 46px;
  background: #12284A;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  margin-bottom: 1.25rem;
}

.verify-btn:hover:not(:disabled) { background: #1a3a6a; }
.verify-btn:active:not(:disabled) { transform: scale(0.99); }
.verify-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.otp-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-size: 0.87rem;
}

.resend-btn, .cancel-btn {
  background: none;
  border: none;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.87rem;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.15s;
}

.resend-btn {
  color: #12284A;
  font-weight: 500;
}

.resend-btn:disabled {
  color: #8a94a6;
  cursor: default;
}

.cancel-btn {
  color: #8a94a6;
}

.cancel-btn:hover { opacity: 0.7; }

.divider {
  color: #d1d5db;
}
</style>